import React, { useState, useRef, useEffect } from 'react';
import { Upload, X, FileText, Image, Tag, Clock, Globe } from 'lucide-react';
import Modal from './Modal';
import { useAppContext } from '../../context/AppContext';
import { hasPermission, PERMISSIONS } from '../../utils/rbac';
import { contentAPI } from '../../services/api';
import { CONTENT_CATEGORIES, VR_PLATFORMS } from '../../constants/enums';
import './UploadModal.css';

const initialForm = {
  title: '',
  subtitle: '',
  description: '',
  category: '',
  platform: '',
  duration: '',
  tags: '',
  apkFile: null,
  thumbnail: null,
  url: '',
};

const UploadModal = () => {
  const { uploadModalOpen, closeUploadModal, showNotification, currentUser } = useAppContext();
  const [form, setForm] = useState(initialForm);
  const [apkName, setApkName] = useState('');
  const [thumbName, setThumbName] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const apkRef = useRef(null);
  const thumbRef = useRef(null);
  const [categories, setCategories] = useState(CONTENT_CATEGORIES);
  const [platforms, setPlatforms] = useState(VR_PLATFORMS);

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const [categoriesRes, platformsRes] = await Promise.all([
          contentAPI.getCategories(),
          contentAPI.getPlatforms()
        ]);
        const fetchedCategories = categoriesRes.data.data;
        const fetchedPlatforms = platformsRes.data.data;
        console.log('Fetched categories:', fetchedCategories);
        console.log('Fetched platforms:', fetchedPlatforms);

        // Use fetched data if non-empty, otherwise fallback to defaults
        setCategories(fetchedCategories && fetchedCategories.length > 0 ? fetchedCategories : CONTENT_CATEGORIES);
        setPlatforms(fetchedPlatforms && fetchedPlatforms.length > 0 ? fetchedPlatforms : VR_PLATFORMS);
      } catch (error) {
        console.error('Failed to fetch options:', error);
        console.log('Using fallback categories and platforms');
        setCategories(CONTENT_CATEGORIES);
        setPlatforms(VR_PLATFORMS);
      }
    };
    fetchOptions();
  }, []);

  useEffect(() => {
    if (uploadModalOpen && !hasPermission(currentUser, PERMISSIONS.UPLOAD_CONTENT)) {
      closeUploadModal();
      showNotification('You do not have permission to upload content', 'error');
    }
  }, [uploadModalOpen, currentUser, closeUploadModal, showNotification]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleApkChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setForm((prev) => ({ ...prev, apkFile: file }));
      setApkName(file.name);
    }
  };

  const handleThumbChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setForm((prev) => ({ ...prev, thumbnail: file }));
      setThumbName(file.name);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Create FormData object
      const formData = new FormData();
      formData.append('title', form.title.trim());
      formData.append('subtitle', form.subtitle.trim());
      formData.append('description', form.description.trim());
      formData.append('category', form.category);
      formData.append('platform', form.platform);
      formData.append('duration', form.duration || '0');

      // Handle tags
      if (form.tags.trim()) {
        formData.append('tags', form.tags.trim());
      }

      // Handle URL for web virtual tours
      if (form.platform === 'Web Browser') {
        if (!form.url.trim()) {
          throw new Error('URL is required for web virtual tours');
        }
        formData.append('url', form.url.trim());
      }

      // Handle APK file for VR platforms
      if (form.platform !== 'Web Browser' && form.apkFile) {
        formData.append('apkFile', form.apkFile);
      } else if (form.platform !== 'Web Browser' && !form.apkFile) {
        throw new Error('APK file is required for VR applications');
      }

      // Handle thumbnail
      if (form.thumbnail) {
        formData.append('thumbnail', form.thumbnail);
      }

      // Use the multipart upload endpoint
      await contentAPI.createContentWithFiles(formData);

      // Success
      setForm(initialForm);
      setApkName('');
      setThumbName('');
      setStep(1);
      closeUploadModal();
      showNotification('Content uploaded successfully!', 'success');
      window.dispatchEvent(new CustomEvent('contentUploaded'));
    } catch (error) {
      console.error('Upload failed:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to upload content';
      showNotification(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setForm(initialForm);
    setApkName('');
    setThumbName('');
    setStep(1);
    closeUploadModal();
  };

  const isStep1Valid =
    form.title.trim() && form.description.trim() && form.category && form.platform;

  const isStep2Valid = form.platform === 'Web Browser' ? form.url.trim() : form.apkFile;

  return (
    <Modal
      isOpen={uploadModalOpen}
      onClose={handleClose}
      title="Upload VR Content"
      size="lg"
      footer={
        <div className="upload-modal__footer-actions">
          {step === 2 && (
            <button className="btn btn-outline" onClick={() => setStep(1)}>
              Back
            </button>
          )}
          <button className="btn btn-ghost" onClick={handleClose}>
            Cancel
          </button>
          {step === 1 ? (
            <button
              className="btn btn-primary"
              onClick={() => setStep(2)}
              disabled={!isStep1Valid}
            >
              Next: Upload Files
            </button>
          ) : (
            <button
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={loading || !isStep2Valid}
            >
              {loading ? (
                <span className="upload-modal__spinner" />
              ) : (
                <>
                  <Upload size={15} />
                  Upload Content
                </>
              )}
            </button>
          )}
        </div>
      }
    >
      {/* Step Indicator */}
      <div className="upload-modal__steps">
        <div className={`upload-modal__step ${step >= 1 ? 'upload-modal__step--active' : ''}`}>
          <span className="upload-modal__step-num">1</span>
          <span>Content Details</span>
        </div>
        <div className="upload-modal__step-line" />
        <div className={`upload-modal__step ${step >= 2 ? 'upload-modal__step--active' : ''}`}>
          <span className="upload-modal__step-num">2</span>
          <span>Upload Files</span>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        {step === 1 && (
          <div className="upload-modal__fields animate-fade-in">
            <div className="form-group">
              <label className="form-label">Content Title *</label>
              <input
                className="form-input"
                type="text"
                name="title"
                value={form.title}
                onChange={handleChange}
                placeholder="e.g., Walkthrough of Train 1"
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Subtitle</label>
              <input
                className="form-input"
                type="text"
                name="subtitle"
                value={form.subtitle}
                onChange={handleChange}
                placeholder="e.g., Complete safety induction for new employees"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Description *</label>
              <textarea
                className="form-textarea"
                name="description"
                value={form.description}
                onChange={handleChange}
                placeholder="Describe what trainees will learn in this VR experience..."
                rows={4}
                required
              />
            </div>

            <div className="upload-modal__row">
              <div className="form-group">
                <label className="form-label">Category *</label>
                <select
                  className="form-select"
                  name="category"
                  value={form.category}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select category</option>
                  {(categories || []).map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">VR Platform *</label>
                <select
                  className="form-select"
                  name="platform"
                  value={form.platform}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select platform</option>
                  {(platforms || []).map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="upload-modal__row">
              <div className="form-group">
                <label className="form-label">
                  <Clock size={13} style={{ display: 'inline', marginRight: 4 }} />
                  Duration (minutes)
                </label>
                <input
                  className="form-input"
                  type="number"
                  name="duration"
                  value={form.duration}
                  onChange={handleChange}
                  placeholder="e.g., 45"
                  min="1"
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  <Tag size={13} style={{ display: 'inline', marginRight: 4 }} />
                  Tags (comma-separated)
                </label>
                <input
                  className="form-input"
                  type="text"
                  name="tags"
                  value={form.tags}
                  onChange={handleChange}
                  placeholder="e.g., endoscopy, procedure, simulation"
                />
              </div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="upload-modal__fields animate-fade-in">
            {/* APK Upload or URL */}
            {form.platform === 'Web Browser' ? (
              <div className="upload-modal__file-zone">
                <div className="upload-modal__file-icon upload-modal__file-icon--url">
                  <Globe size={28} />
                </div>
                <div className="upload-modal__file-info">
                  <span className="upload-modal__file-label">Web Virtual Tour URL *</span>
                  <input
                    className="form-input"
                    type="url"
                    name="url"
                    value={form.url}
                    onChange={handleChange}
                    placeholder="https://tour.nlng.com/plant-360"
                    required
                  />
                  <span className="upload-modal__file-hint">Enter the full URL of the web virtual tour</span>
                </div>
              </div>
            ) : (
              <div className="upload-modal__file-zone" onClick={() => apkRef.current?.click()}>
                <input
                  ref={apkRef}
                  type="file"
                  accept=".apk"
                  onChange={handleApkChange}
                  style={{ display: 'none' }}
                />
                <div className="upload-modal__file-icon upload-modal__file-icon--apk">
                  <FileText size={28} />
                </div>
                {apkName ? (
                  <div className="upload-modal__file-info">
                    <span className="upload-modal__file-name">{apkName}</span>
                    <span className="upload-modal__file-change">Click to change</span>
                  </div>
                ) : (
                  <div className="upload-modal__file-info">
                    <span className="upload-modal__file-label">Drop APK file here or click to browse *</span>
                    <span className="upload-modal__file-hint">Accepts .apk files only</span>
                  </div>
                )}
              </div>
            )}

            {/* Thumbnail Upload */}
            <div className="upload-modal__file-zone" onClick={() => thumbRef.current?.click()}>
              <input
                ref={thumbRef}
                type="file"
                accept="image/*"
                onChange={handleThumbChange}
                style={{ display: 'none' }}
              />
              <div className="upload-modal__file-icon upload-modal__file-icon--img">
                <Image size={28} />
              </div>
              {thumbName ? (
                <div className="upload-modal__file-info">
                  <span className="upload-modal__file-name">{thumbName}</span>
                  <span className="upload-modal__file-change">Click to change</span>
                </div>
              ) : (
                <div className="upload-modal__file-info">
                  <span className="upload-modal__file-label">Upload Thumbnail (optional)</span>
                  <span className="upload-modal__file-hint">PNG, JPG or WEBP — 16:9 recommended</span>
                </div>
              )}
            </div>

            {/* Summary */}
            <div className="upload-modal__summary">
              <h4 className="upload-modal__summary-title">Content Summary</h4>
              <div className="upload-modal__summary-grid">
                <div><span>Title</span><strong>{form.title}</strong></div>
                <div><span>Subtitle</span><strong>{form.subtitle || '—'}</strong></div>
                <div><span>Category</span><strong>{form.category}</strong></div>
                <div><span>Platform</span><strong>{form.platform}</strong></div>
                <div><span>Duration</span><strong>{form.duration ? `${form.duration} min` : '—'}</strong></div>
              </div>
            </div>
          </div>
        )}
      </form>
    </Modal>
  );
};

export default UploadModal;
